/**
 * Mutation Error Types
 *
 * Structured error types for client-side mutation handling using Effect's Data.TaggedError.
 * These errors provide both user-friendly messages and detailed developer information.
 *
 * @module
 */

import { Data } from "effect";
import { decodeFhirResourceAlreadyExistsMessage } from "@ascleo/fhir-rpc-contracts/resource-already-exists";
import { decodeFhirResourceIdUnavailableMessage } from "@ascleo/fhir-rpc-contracts/resource-id-unavailable";
import { decodeFhirResourceNotFoundMessage } from "@ascleo/fhir-rpc-contracts/resource-not-found";
import { decodeFhirRevisionConflictMessage } from "@ascleo/fhir-rpc-contracts/revision-conflict";
import { toast } from "sonner";
import { diagnosticLogger } from "../lib/diagnostic-logger";
import { devErrorStore } from "../stores/dev-error.store";
import { decodeUserFacingRefusal } from "@ascleo/types/user-facing-refusal";

/**
 * Base mutation error for general mutation failures.
 * Used when a mutation fails for reasons other than schema validation or RPC issues.
 */
export class MutationError extends Data.TaggedError("MutationError")<{
  /** User-friendly error message suitable for display in UI */
  readonly userMessage: string;
  /** Detailed message for developers/debugging */
  readonly developerMessage: string;
  /** Optional path to the field that caused the error */
  readonly path?: readonly string[];
  /** Original error that caused this mutation error */
  readonly cause?: unknown;
}> {}

/**
 * Schema validation error for when data fails Effect Schema validation.
 * Contains detailed information about which fields failed and why.
 */
export class SchemaValidationError extends Data.TaggedError(
  "SchemaValidationError"
)<{
  /** User-friendly error message suitable for display in UI */
  readonly userMessage: string;
  /** Detailed message for developers/debugging */
  readonly developerMessage: string;
  /** List of individual field validation errors */
  readonly errors: ReadonlyArray<{
    readonly path: readonly string[];
    readonly message: string;
  }>;
}> {}

/**
 * RPC error for when a remote procedure call fails.
 * Captures the RPC method name and original cause for debugging.
 */
export class RpcError extends Data.TaggedError("RpcError")<{
  /** User-friendly error message suitable for display in UI */
  readonly userMessage: string;
  /** Detailed message for developers/debugging */
  readonly developerMessage: string;
  /** Name of the RPC method that failed (e.g., "PatientCreate") */
  readonly rpcMethod: string;
  /** Original error that caused this RPC error */
  readonly cause?: unknown;
}> {}

/**
 * A versioned write was refused because the revision the user read is no longer
 * current: someone else changed or deleted the resource in the meantime.
 *
 * Produced only at the RPC ingress ({@link wrapAsRpcError}) from the server's
 * stale-revision refusal; consumers match the tag instead of reading messages.
 *
 * @since 2026-10-06
 * @example
 * ```ts
 * const conflict = new RevisionConflictError({
 *   userMessage: "Der Datensatz wurde zwischenzeitlich geändert.",
 *   developerMessage: "TaskDelete: expected revision 3, current 4",
 *   rpcMethod: "TaskDelete",
 *   resourceType: "Task",
 *   expectedRevision: 3,
 *   actualRevision: 4,
 * });
 * ```
 */
export class RevisionConflictError extends Data.TaggedError(
  "RevisionConflictError"
)<{
  /** User-friendly error message suitable for display in UI */
  readonly userMessage: string;
  /** Detailed message for developers/debugging */
  readonly developerMessage: string;
  /** Name of the RPC method that was refused (e.g., "TaskDelete") */
  readonly rpcMethod: string;
  /** Resource type of the refused write */
  readonly resourceType: string;
  /** The revision the caller read and submitted */
  readonly expectedRevision: number;
  /** The revision current when the write was refused */
  readonly actualRevision: number;
}> {}

/**
 * A create was refused because a resource with the requested id already
 * exists (the table's primary key, nothing else).
 *
 * Produced only at the RPC ingress ({@link wrapAsRpcError}) from the server's
 * typed refusal; consumers match the tag instead of reading messages.
 *
 * @since 2026-10-22
 * @example
 * ```ts
 * const existing = new ResourceAlreadyExistsError({
 *   userMessage: "Der Datensatz existiert bereits.",
 *   developerMessage: "ChargeItemCreate: ChargeItem/ci-1 already exists",
 *   rpcMethod: "ChargeItemCreate",
 *   resourceType: "ChargeItem",
 *   resourceId: "ci-1",
 * });
 * ```
 */
export class ResourceAlreadyExistsError extends Data.TaggedError(
  "ResourceAlreadyExistsError"
)<{
  /** User-friendly error message suitable for display in UI */
  readonly userMessage: string;
  /** Detailed message for developers/debugging */
  readonly developerMessage: string;
  /** Name of the RPC method that was refused (e.g., "ChargeItemCreate") */
  readonly rpcMethod: string;
  /** Resource type of the refused create */
  readonly resourceType: string;
  /** The id that already exists */
  readonly resourceId: string;
}> {}

/**
 * A read found no resource with the requested id in the caller's scope (never
 * created, deleted, or another tenant's).
 *
 * Produced only at the RPC ingress ({@link wrapAsRpcError}) from the server's
 * typed refusal; a read facade turns it into "absent" exactly once.
 *
 * @since 2026-10-24
 * @example
 * ```ts
 * const missing = new ResourceNotFoundError({
 *   userMessage: "Der Datensatz ist nicht (mehr) vorhanden.",
 *   developerMessage: "ChargeItemDetail: ChargeItem/ci-1 not found",
 *   rpcMethod: "ChargeItemDetail",
 *   resourceType: "ChargeItem",
 *   resourceId: "ci-1",
 * });
 * ```
 */
export class ResourceNotFoundError extends Data.TaggedError(
  "ResourceNotFoundError"
)<{
  /** User-friendly error message suitable for display in UI */
  readonly userMessage: string;
  /** Detailed message for developers/debugging */
  readonly developerMessage: string;
  /** Name of the RPC method that failed (e.g., "ChargeItemDetail") */
  readonly rpcMethod: string;
  /** Resource type of the read */
  readonly resourceType: string;
  /** The id that was not found */
  readonly resourceId: string;
}> {}

/**
 * A create was refused because the requested id is held by a resource outside
 * the caller's scope (another tenant's row). Not "already exists": nothing of
 * the caller's landed, so the caller keeps its data and uses a fresh id.
 *
 * Produced only at the RPC ingress ({@link wrapAsRpcError}).
 *
 * @since 2026-10-24
 * @example
 * ```ts
 * const unavailable = new ResourceIdUnavailableError({
 *   userMessage: "Die Kennung ist vergeben.",
 *   developerMessage: "ChargeItemCreate: ChargeItem/ci-1 id unavailable",
 *   rpcMethod: "ChargeItemCreate",
 *   resourceType: "ChargeItem",
 *   resourceId: "ci-1",
 * });
 * ```
 */
export class ResourceIdUnavailableError extends Data.TaggedError(
  "ResourceIdUnavailableError"
)<{
  /** User-friendly error message suitable for display in UI */
  readonly userMessage: string;
  /** Detailed message for developers/debugging */
  readonly developerMessage: string;
  /** Name of the RPC method that was refused (e.g., "ChargeItemCreate") */
  readonly rpcMethod: string;
  /** Resource type of the refused create */
  readonly resourceType: string;
  /** The id that cannot be used */
  readonly resourceId: string;
}> {}

/**
 * Collection error for when a TanStack DB collection operation fails.
 * Captures the collection ID and operation type for debugging.
 *
 * @since 0.1.0
 */
export class CollectionError extends Data.TaggedError("CollectionError")<{
  /** User-friendly error message suitable for display in UI */
  readonly userMessage: string;
  /** Detailed message for developers/debugging */
  readonly developerMessage: string;
  /** Identifier of the collection that failed (e.g., "patient") */
  readonly collectionId: string;
  /** The operation that failed */
  readonly operation: "insert" | "update" | "delete";
  /** Optional resource ID involved in the operation */
  readonly resourceId?: string;
  /** Original error that caused this collection error */
  readonly cause?: unknown;
}> {}

/**
 * Union type of all mutation-related errors for exhaustive pattern matching.
 */
export type MutationErrorUnion =
  | MutationError
  | SchemaValidationError
  | RpcError
  | RevisionConflictError
  | ResourceAlreadyExistsError
  | ResourceNotFoundError
  | ResourceIdUnavailableError
  | CollectionError;

/**
 * Type guard to check if an error is a MutationError
 */
export function isMutationError(error: unknown): error is MutationError {
  return (
    error instanceof MutationError ||
    (typeof error === "object" &&
      error !== null &&
      "_tag" in error &&
      error._tag === "MutationError")
  );
}

/**
 * Type guard to check if an error is a SchemaValidationError
 */
export function isSchemaValidationError(
  error: unknown
): error is SchemaValidationError {
  return (
    error instanceof SchemaValidationError ||
    (typeof error === "object" &&
      error !== null &&
      "_tag" in error &&
      error._tag === "SchemaValidationError")
  );
}

/**
 * Type guard to check if an error is an RpcError
 */
export function isRpcError(error: unknown): error is RpcError {
  return (
    error instanceof RpcError ||
    (typeof error === "object" &&
      error !== null &&
      "_tag" in error &&
      error._tag === "RpcError")
  );
}

/**
 * Type guard to check if an error is a stale-revision refusal.
 *
 * @since 2026-10-06
 * @example
 * ```ts
 * if (isRevisionConflictError(error)) reloadDisplayedRow();
 * ```
 */
export function isRevisionConflictError(
  error: unknown
): error is RevisionConflictError {
  return (
    error instanceof RevisionConflictError ||
    (typeof error === "object" &&
      error !== null &&
      "_tag" in error &&
      error._tag === "RevisionConflictError")
  );
}

/**
 * Type guard to check if an error is a create refused on an existing id.
 *
 * @since 2026-10-22
 * @example
 * ```ts
 * if (isResourceAlreadyExistsError(error)) adoptExistingRow(error.resourceId);
 * ```
 */
export function isResourceAlreadyExistsError(
  error: unknown
): error is ResourceAlreadyExistsError {
  return (
    error instanceof ResourceAlreadyExistsError ||
    (typeof error === "object" &&
      error !== null &&
      "_tag" in error &&
      error._tag === "ResourceAlreadyExistsError")
  );
}

/**
 * Type guard to check if an error is a read of a missing resource.
 *
 * @since 2026-10-24
 * @example
 * ```ts
 * if (isResourceNotFoundError(error)) return undefined;
 * ```
 */
export function isResourceNotFoundError(
  error: unknown
): error is ResourceNotFoundError {
  return (
    error instanceof ResourceNotFoundError ||
    (typeof error === "object" &&
      error !== null &&
      "_tag" in error &&
      error._tag === "ResourceNotFoundError")
  );
}

/**
 * Type guard to check if an error is a create refused on an id held outside
 * the caller's scope.
 *
 * @since 2026-10-24
 * @example
 * ```ts
 * if (isResourceIdUnavailableError(error)) retryUnderFreshId();
 * ```
 */
export function isResourceIdUnavailableError(
  error: unknown
): error is ResourceIdUnavailableError {
  return (
    error instanceof ResourceIdUnavailableError ||
    (typeof error === "object" &&
      error !== null &&
      "_tag" in error &&
      error._tag === "ResourceIdUnavailableError")
  );
}

/**
 * The developer message of any error produced at the RPC ingress
 * ({@link wrapAsRpcError}): a plain RPC failure or one of its typed refusals.
 * `undefined` for anything else. For reports and diagnostics only — it is the
 * server's or the client's English technical text; what the user sees comes
 * from {@link rpcFailureDisplayMessage}.
 *
 * @since 2026-10-24
 * @example
 * ```ts
 * const detail = rpcDeveloperMessage(error) ?? String(error);
 * ```
 */
export function rpcDeveloperMessage(error: unknown): string | undefined {
  return isRpcError(error) ||
    isRevisionConflictError(error) ||
    isResourceAlreadyExistsError(error) ||
    isResourceNotFoundError(error) ||
    isResourceIdUnavailableError(error)
    ? error.developerMessage
    : undefined;
}

/**
 * The one rule for what the user sees after a failed call (German UI):
 * 1. a server refusal with an allowlisted coded reason (`[code] reason`,
 *    `@ascleo/types/user-facing-refusal`) — its German reason is shown (the
 *    code stays in reports), it is the actual reason (e.g. a closed release
 *    gate, a wrong AzP PIN); unknown codes are technical text;
 * 2. a typed refusal from the RPC ingress — its German user message (not
 *    found, stale revision, existing id, id held elsewhere);
 * 3. anything else — `fallback`, the caller's German description of what
 *    failed. Technical texts (developer messages, plain `Error` messages,
 *    server exceptions) are never shown; they belong in
 *    `errorService.report` / {@link rpcDeveloperMessage}.
 *
 * @since 2026-10-24
 * @example
 * ```ts
 * setMessage(rpcFailureDisplayMessage(error, "Import konnte nicht ausgeführt werden."));
 * ```
 */
export function rpcFailureDisplayMessage(
  error: unknown,
  fallback: string
): string {
  const coded = decodeUserFacingRefusal(rpcDeveloperMessage(error));
  if (coded !== undefined) {
    // The German reason only; the code stays in the report for diagnostics.
    return coded.reason;
  }
  if (
    isRevisionConflictError(error) ||
    isResourceAlreadyExistsError(error) ||
    isResourceNotFoundError(error) ||
    isResourceIdUnavailableError(error)
  ) {
    return error.userMessage;
  }
  return fallback;
}

/**
 * A German context sentence for a failure, followed by the reason the shared
 * display rule allows ({@link rpcFailureDisplayMessage}: an allowlisted coded
 * reason or a typed refusal) — or the context alone, never with an empty
 * trailing reason or a technical text.
 *
 * @since 2026-10-24
 * @example
 * ```ts
 * rpcFailureDisplayWithContext("Status konnte nicht geladen werden.", error);
 * // "Status konnte nicht geladen werden." or "… Der Datensatz ist nicht (mehr) vorhanden."
 * ```
 */
export function rpcFailureDisplayWithContext(
  context: string,
  error: unknown
): string {
  const reason = rpcFailureDisplayMessage(error, "");
  return reason === "" ? context : `${context} ${reason}`;
}

/**
 * Type guard to check if an error is a CollectionError
 *
 * @since 0.1.0
 */
export function isCollectionError(error: unknown): error is CollectionError {
  return (
    error instanceof CollectionError ||
    (typeof error === "object" &&
      error !== null &&
      "_tag" in error &&
      error._tag === "CollectionError")
  );
}

/**
 * Type guard to check if an error is any mutation-related error
 */
export function isMutationErrorUnion(
  error: unknown
): error is MutationErrorUnion {
  return (
    isMutationError(error) ||
    isSchemaValidationError(error) ||
    isRpcError(error) ||
    isRevisionConflictError(error) ||
    isResourceAlreadyExistsError(error) ||
    isResourceNotFoundError(error) ||
    isResourceIdUnavailableError(error) ||
    isCollectionError(error)
  );
}

/**
 * Display a mutation error to the user via toast notification.
 * Shows a user-friendly message with an option to view developer details.
 *
 * @param error - The mutation error to display
 * @param options - Optional configuration for the toast
 */
export function displayMutationError(
  error: MutationErrorUnion,
  options?: {
    /** Whether to log the full error to console (default: true) */
    logToConsole?: boolean;
  }
): void {
  const { logToConsole = true } = options ?? {};

  // Determine layer from error tag
  const layer =
    isRpcError(error) ||
    isRevisionConflictError(error) ||
    isResourceAlreadyExistsError(error) ||
    isResourceNotFoundError(error) ||
    isResourceIdUnavailableError(error)
    ? "rpc" as const
    : isCollectionError(error)
      ? "collection" as const
      : "mutation" as const;

  const source =
    isRpcError(error) ||
    isRevisionConflictError(error) ||
    isResourceAlreadyExistsError(error) ||
    isResourceNotFoundError(error) ||
    isResourceIdUnavailableError(error)
    ? error.rpcMethod
    : isCollectionError(error)
      ? `${error.collectionId}.${error.operation}`
      : "mutation";

  // Push to dev error store
  devErrorStore.push({
    error,
    userMessage: error.userMessage,
    developerMessage: error.developerMessage,
    layer,
    source,
    stack: error instanceof Error ? error.stack : undefined,
  });

  // Log full error details to console for debugging
  if (logToConsole) {
    diagnosticLogger.error("[MutationError]", {
      tag: error._tag,
      hasUserMessage: error.userMessage.length > 0,
      hasDeveloperMessage: error.developerMessage.length > 0,
      ...(isSchemaValidationError(error) && { validationErrorCount: error.errors.length }),
      ...(isRpcError(error) && { rpcMethod: error.rpcMethod }),
      ...(isCollectionError(error) && { collectionId: error.collectionId, operation: error.operation }),
      ...("cause" in error && error.cause !== undefined && { hasCause: true }),
    });
  }

  // Show toast with user-friendly message
  toast.error(error.userMessage, {
    description: error.developerMessage,
    duration: 5000,
    action: {
      label: "Kopieren",
      onClick: () => {
        const details = JSON.stringify({
          tag: error._tag,
          userMessage: error.userMessage,
          developerMessage: error.developerMessage,
          ...(isSchemaValidationError(error) && { errors: error.errors }),
          ...(isRpcError(error) && { rpcMethod: error.rpcMethod }),
          ...(isCollectionError(error) && { collectionId: error.collectionId, operation: error.operation }),
        }, null, 2);
        navigator.clipboard.writeText(details);
      },
    },
  });
}

/** German label of a mutation operation for user messages. */
function operationUserLabel(operation: string): string {
  switch (operation) {
    case "create":
    case "insert":
      return "Anlegen";
    case "delete":
      return "Löschen";
    default:
      return "Speichern";
  }
}

/**
 * Create a MutationError from an unknown error.
 * Useful for wrapping unexpected errors in a consistent format.
 *
 * @param error - The unknown error to wrap
 * @param context - Context about where the error occurred
 */
export function wrapAsMutationError(
  error: unknown,
  context: { operation: string; resourceType: string }
): MutationError {
  const message =
    error instanceof Error ? error.message : "An unexpected error occurred";

  return new MutationError({
    // User text is German (the UI language); the developer text stays English.
    userMessage: `${operationUserLabel(context.operation)} fehlgeschlagen. Bitte erneut versuchen.`,
    developerMessage: message,
    cause: error,
  });
}

/**
 * Create the client error for a failed RPC call. This is the RPC ingress: the
 * server's typed refusals are decoded once — not found into
 * {@link ResourceNotFoundError}, an id held elsewhere into
 * {@link ResourceIdUnavailableError}, an existing id into
 * {@link ResourceAlreadyExistsError}, a stale revision into
 * {@link RevisionConflictError}; every other failure becomes an
 * {@link RpcError}.
 *
 * @param error - The unknown error to wrap
 * @param rpcMethod - Name of the RPC method that failed
 * @param resourceType - Type of resource being operated on
 * @since 0.1.0
 * @example
 * ```ts
 * const wrapped = wrapAsRpcError(error, "TaskDelete", "Task");
 * if (isRevisionConflictError(wrapped)) console.log(wrapped.actualRevision);
 * ```
 */
export function wrapAsRpcError(
  error: unknown,
  rpcMethod: string,
  resourceType: string
):
  | RpcError
  | RevisionConflictError
  | ResourceAlreadyExistsError
  | ResourceNotFoundError
  | ResourceIdUnavailableError {
  const missing = decodeFhirResourceNotFoundMessage(error);
  if (missing !== undefined) {
    return new ResourceNotFoundError({
      userMessage: "Der Datensatz ist nicht (mehr) vorhanden.",
      // The server's text itself, unchanged for anything that displays it.
      developerMessage: typeof error === "string" ? error : `${rpcMethod}: ${missing.resourceType}/${missing.id} not found`,
      rpcMethod,
      resourceType: missing.resourceType,
      resourceId: missing.id,
    });
  }
  const unavailable = decodeFhirResourceIdUnavailableMessage(error);
  if (unavailable !== undefined) {
    return new ResourceIdUnavailableError({
      userMessage: "Die Kennung ist bereits anderweitig vergeben.",
      developerMessage: `${rpcMethod}: ${unavailable.resourceType}/${unavailable.id} id unavailable`,
      rpcMethod,
      resourceType: unavailable.resourceType,
      resourceId: unavailable.id,
    });
  }
  const existing = decodeFhirResourceAlreadyExistsMessage(error);
  if (existing !== undefined) {
    return new ResourceAlreadyExistsError({
      userMessage: "Der Datensatz existiert bereits.",
      developerMessage: `${rpcMethod}: ${existing.resourceType}/${existing.id} already exists`,
      rpcMethod,
      resourceType: existing.resourceType,
      resourceId: existing.id,
    });
  }
  const conflict = decodeFhirRevisionConflictMessage(error);
  if (conflict !== undefined) {
    return new RevisionConflictError({
      userMessage:
        "Der Datensatz wurde zwischenzeitlich geändert. Bitte die aktuelle Fassung prüfen und erneut versuchen.",
      developerMessage: `${rpcMethod}: ${resourceType} expected revision ${conflict.expectedRevision}, current ${conflict.actualRevision}`,
      rpcMethod,
      resourceType,
      expectedRevision: conflict.expectedRevision,
      actualRevision: conflict.actualRevision,
    });
  }

  let message: string;
  if (typeof error === "string") {
    message = error;
  } else if (error instanceof Error) {
    // Some external errors carry no message; retain their string representation.
    message = error.message || String(error);
  } else {
    message = String(error) || "RPC call failed";
  }

  return new RpcError({
    // Neutral: the ingress also wraps reads.
    userMessage: "Vorgang fehlgeschlagen. Bitte erneut versuchen.",
    developerMessage: message,
    rpcMethod,
    cause: error,
  });
}
